import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store";
import { useEffect, useState } from "react";
import { deleteTrip, fetchTrips } from "../../features/admin/tripSlice";
import InnerLoading from "../ui/InnerLoading";
import { Link } from "react-router-dom";
import ConfirmDeleteModal from "./models/ConfirmDeleteModal";
import { FaEdit, FaEye, FaStar, FaTrashAlt } from "react-icons/fa";
import ScrollButton from "../ui/ScrollButton";
import Pagination from "../ui/Pagination";
import { formatDateRange } from "../../utils/data";

const Trip = () => {
	const { t } = useTranslation();
	const dispatch = useDispatch<AppDispatch>();
	const { trips,pagination, loading } = useSelector((state: RootState) => state.tripSlice);
	const { user } = useSelector((state: RootState) => state.user);
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);
	const [selectedStayId, setSelectedStayId] = useState<number | null>(null);
	useEffect(() => {
		dispatch(fetchTrips({ userId: user?.id, page: 1 }));
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch]);
	const handleDelete = async () => {
		if (selectedStayId !== null) {
			await dispatch(deleteTrip(selectedStayId));
			dispatch(fetchTrips({ userId: user?.id, page: 1 }));
			setSelectedStayId(null);
			setDeleteModalOpen(false);
		}
	};
	const confirmDelete = (id: number) => {
		setSelectedStayId(id);
		setDeleteModalOpen(true);
	};
	return (
		<div>
			<div className='bg-gradient-to-r from-blue-600 to-indigo-500 text-white rounded-xl shadow-md p-6 mb-6'>
				<h2 className='text-white text-lg font-semibold'>{t("trip-title")}</h2>
			</div>
			{loading ? (
				<InnerLoading />
			) : trips.length === 0 ? (
				<p className="text-center text-gray-500">{t("no-trips")}</p>
			) : (
				<div className="space-y-6 bg-white p-4">
					{trips?.map((trip) => (
						<div key={trip.id} className="bg-white relative border rounded-2xl overflow-hidden flex flex-col md:flex-row transition hover:shadow-xl">
							<div className="md:w-1/3 w-full h-64 md:h-auto">
								<img
									src={trip.tripImages?.[0]?.url || "/tripholder.jpg"}
									alt={trip?.fromLocation}
									className="w-full h-full object-cover"
								/>
							</div>
							<div className="p-6 flex flex-col justify-between flex-1">
								<div>
									<h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">{trip?.fromLocation} - {trip?.toLocation}</h2>
									<div className="text-gray-600 dark:text-gray-300 space-y-1 text-sm md:text-base">
										<p>{t("price")}: <strong>{trip.price} {trip.currencyCode}</strong></p>
										<p>{t("dates")}: {formatDateRange(trip.fromDate,trip.toDate)}</p>
										<p>{t("stay")}: {trip.place?.title}</p>
										<p className=" flex items-center gap-1">{t("rating")}: {trip.rate.toFixed(1).replace(/\.0$/, "")}  <FaStar className=" text-orange-400" /></p>
										<p className="line-clamp-2">{trip.details}</p>
										{trip.subCategory && <p className=" absolute top-2 left-5 text-white bg-primary rounded-lg py-1 px-2"> {trip.subCategory?.title}</p>}
									</div>
								</div>
								<div className="flex flex-wrap gap-2 pt-2">
									<Link to={`/dashboard/trip-details/${trip.id}`}
										className="flex items-center gap-1 text-sm px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded"
									>
										<FaEye /> {t("show")}
									</Link>
									<Link to={`/dashboard/trip-modal/${trip.id}`}
										className="flex items-center gap-1 text-sm px-3 py-1 bg-yellow-100 text-yellow-800 hover:bg-yellow-200 rounded">
										<FaEdit /> {t("edit")}
									</Link>
									<button
										onClick={() => confirmDelete(trip.id)}
										className="flex items-center gap-1 text-sm px-3 py-1 bg-red-100 text-red-600 hover:bg-red-200 rounded"
									>
										<FaTrashAlt /> {t("delete")}
									</button>
								</div>
							</div>
						</div>
					))}
					<Pagination
						currentPage={pagination?.currentPage || 1}
						lastPage={pagination?.lastPage || 1}
						onPageChange={(page) => dispatch(fetchTrips({ userId: user?.id, page }))}
					/>
					<div className="flex justify-end w-full pt-6 col-span-full">
						<Link
							to={`/dashboard/trip-modal`}
							className="bg-primary px-6 py-2 rounded-full text-white hover:bg-darkColor transition"
						>
							+ {t("add-new-Trip")}
						</Link>
					</div>
					<ConfirmDeleteModal
						isOpen={deleteModalOpen}
						onClose={() => setDeleteModalOpen(false)}
						onConfirm={handleDelete}
						messageKey="delete-trip-confirm"
					/>
				</div>
			)}
			<ScrollButton />
		</div>
	);
};

export default Trip;
